import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../services/api';
import { Medicine } from '../types';

const Medicines: React.FC = () => {
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newMedicine, setNewMedicine] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const fetchMedicines = async () => {
    setLoading(true);
    try {
      const res = await api.getMedicines({ active: 'all' });
      setMedicines(res.data?.medicines || []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load medicines', severity: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMedicines();
  }, []);

  const handleAddMedicine = async () => {
    if (!newMedicine.trim()) {
      setSnackbar({ open: true, message: 'Medicine name is required', severity: 'error' });
      return;
    }
    try {
      await api.createMedicine({ MedicineName: newMedicine });
      setSnackbar({ open: true, message: 'Medicine added successfully', severity: 'success' });
      setOpenAddDialog(false);
      setNewMedicine('');
      fetchMedicines();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to add medicine', severity: 'error' });
    }
  };

  const handleDeleteMedicine = async (id: number) => {
    setDeletingId(id);
    try {
      await api.deleteMedicine(id);
      setSnackbar({ open: true, message: 'Medicine deleted successfully', severity: 'success' });
      fetchMedicines();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete medicine', severity: 'error' });
    }
    setDeletingId(null);
    setConfirmDelete({ open: false, id: null });
  };

  const handleToggleActive = async (medicine: Medicine) => {
    setUpdatingId(medicine.ID);
    try {
      await api.updateMedicineStatus(medicine.ID, medicine.IsActive ? 0 : 1);
      setSnackbar({ open: true, message: `Medicine marked as ${medicine.IsActive ? 'Inactive' : 'Active'}`, severity: 'success' });
      fetchMedicines();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
    setUpdatingId(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Medicines
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddDialog(true)}>
          Add Medicine
        </Button>
      </Box>
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Medicine Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {medicines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No medicines found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    medicines.map((medicine, idx) => (
                      <TableRow key={medicine.ID}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{medicine.MedicineName}</TableCell>
                        <TableCell>
                          <Switch
                            checked={!!medicine.IsActive}
                            color="success"
                            onChange={() => handleToggleActive(medicine)}
                            disabled={updatingId === medicine.ID}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => setConfirmDelete({ open: true, id: medicine.ID })}
                            disabled={deletingId === medicine.ID}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Medicine Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Medicine</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Medicine Name"
            fullWidth
            value={newMedicine}
            onChange={(e) => setNewMedicine(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddMedicine} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, id: null })}>
        <DialogTitle>Delete Medicine</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this medicine?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, id: null })}>Cancel</Button>
          <Button
            onClick={() => confirmDelete.id && handleDeleteMedicine(confirmDelete.id)}
            color="error"
            variant="contained"
            disabled={deletingId === confirmDelete.id}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Medicines; 